import { buildAuditFormJson, createEmptyRootLevel } from 'docuty-template-renderer';
import { FormJson } from '../../api/model/formJson';

/**
 * DOCU-1358: Hilfsfunktionen für den Dialog „Neue Vorlage anlegen“.
 *
 * Der Dialog legt eine Vorlage entweder leer an (danach geht es in den
 * Designer) oder übernimmt ein vorhandenes Formular als JSON. Ein
 * `entryFormJson` gibt es nicht mehr (ADR 0001, DOCU-529).
 */

/** Ergebnis, mit dem der Dialog geschlossen wird. */
export interface CreateTemplateResult {
  id: string;
  /** true = leer angelegt, der Aufrufer soll den Designer öffnen. */
  openDesigner: boolean;
}

/** Optionale Daten beim Öffnen; `orgId` trägt die Organisation vor. */
export interface CreateTemplateDialogData {
  orgId?: string | null;
}

/** Query-Parameter, mit dem die Detailseite den Designer sofort öffnet. */
export const OPEN_DESIGNER_PARAM = 'designer';

/** Navigations-Extras zur Detailseite nach dem Anlegen. */
export function designerQueryParams(result: CreateTemplateResult): { queryParams?: Record<string, string> } {
  return result.openDesigner ? { queryParams: { [OPEN_DESIGNER_PARAM]: '1' } } : {};
}

export type ParseFormJsonResult =
  | { ok: true; form: FormJson }
  | { ok: false; error: 'EMPTY' | 'INVALID_JSON' | 'NOT_A_FORM' };

/**
 * Liest ein Formular aus eingefügtem Text oder einer geladenen Datei.
 *
 * Akzeptiert das Formular selbst (`{ id, fields, children, uiDescriptor … }`)
 * und eine ganze Vorlage, wie sie z. B. aus der API kommt
 * (`{ name, auditFormJson: { … } }`) — dann zählt nur `auditFormJson`.
 */
export function parseFormJsonInput(text: string): ParseFormJsonResult {
  const trimmed = (text ?? '').replace(/^﻿/, '').trim();
  if (!trimmed) {
    return { ok: false, error: 'EMPTY' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: 'INVALID_JSON' };
  }

  if (!isPlainObject(parsed)) {
    return { ok: false, error: 'NOT_A_FORM' };
  }

  const candidate = isPlainObject(parsed['auditFormJson']) ? parsed['auditFormJson'] : parsed;
  if (!looksLikeForm(candidate)) {
    return { ok: false, error: 'NOT_A_FORM' };
  }

  return { ok: true, form: candidate as FormJson };
}

/**
 * Leeres, aber vollständiges Formular — inklusive `uiDescriptor.listItem`
 * und `uiDescriptor.confirmDialog`, ohne die eine Vorlage in der App nicht
 * nutzbar ist. Gebaut von derselben Funktion, mit der der Designer speichert.
 */
export function createEmptyAuditFormJson(name: string, description: string): FormJson {
  return buildAuditFormJson({
    rootLevel: createEmptyRootLevel(),
    formTitle: name,
    description,
  }) as FormJson;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Mindestens eines der Strukturmerkmale eines Formulars muss vorhanden sein. */
function looksLikeForm(value: Record<string, unknown>): boolean {
  return Array.isArray(value['fields'])
    || Array.isArray(value['children'])
    || isPlainObject(value['uiDescriptor']);
}
